import { useNavigate } from 'react-router-dom'

import { Space } from '@tribeplatform/gql-client/types'
import { useDeleteCollection } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { Text } from '@tribeplatform/react-ui-kit/Text'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

export const CollectionDeleteModal = ({
  id,
  name,
  spaces,
  open,
  skipNavigate,
  onClose,
}: {
  id: string
  name: string
  spaces: Space[]
  open: boolean
  skipNavigate?: boolean
  onClose: () => void
}) => {
  const { mutateAsync: deleteCollection, isLoading } = useDeleteCollection({
    fields: 'basic',
  })
  const hasSpaces = !!spaces?.length
  const navigate = useNavigate()
  const { $t } = useI18n()

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={$t(
          {
            id: 'Collection.CollectionDeleteModal.HeaderTitle',
            defaultMessage: 'Delete {collection_name} {COLLECTION}',
          },
          { collection_name: name },
        )}
      />
      <Modal.Content>
        {hasSpaces ? (
          <Text>
            <T
              id="Collection.CollectionDeleteModal.FirstRemoveSpaces"
              defaultMessage="To delete this {COLLECTION}, you have to first remove the {SPACES} within it. Go to each {SPACE}’s settings to either delete the {SPACES} or move them to a different {COLLECTION}."
            />
          </Text>
        ) : (
          <Text>
            <T
              id="Collection.CollectionDeleteModal.AreYouSure"
              defaultMessage="Are you sure you want to delete {collection_name} {COLLECTION}?"
              values={{
                collection_name: name,
              }}
            />
          </Text>
        )}
      </Modal.Content>
      {hasSpaces ? (
        <Modal.Actions>
          <Button variant="secondary" onClick={onClose}>
            <T
              id="Collection.CollectionDeleteModal.GotItButtonText"
              defaultMessage="Got It"
            />
          </Button>
        </Modal.Actions>
      ) : (
        <Modal.Actions>
          <Button
            loading={isLoading}
            onClick={e => {
              e.stopPropagation()
              deleteCollection({ id })
                .then(() => {
                  onClose()
                  if (!skipNavigate) {
                    navigate('/')
                  }
                })
                .catch(() => {
                  onClose()
                  toast({
                    title: $t({
                      id: 'Generics.Error',
                      defaultMessage: 'Error',
                    }),
                    description: $t({
                      id: 'Collection.CollectionDeleteModal.ErrorDescription',
                      defaultMessage: 'Could not delete {COLLECTION}',
                    }),
                    status: 'error',
                  })
                })
            }}
          >
            <T id="Generics.Confirm" defaultMessage="Confirm" />
          </Button>
          <Button variant="secondaryNeutral" onClick={onClose}>
            <T id="Generics.Cancel" defaultMessage="Cancel" />
          </Button>
        </Modal.Actions>
      )}
    </Modal>
  )
}
